import { handleSessionExpiredUnauthorized } from './authService';

/** Anything with `interceptors.response.use` — the global axios object or an instance. */
type ResponseInterceptable = {
    interceptors: { response: { use: (onFulfilled: (r: any) => any, onRejected: (e: any) => any) => unknown } };
};

/**
 * Wire session-expiry handling onto every axios client the app uses.
 *
 * Both branches matter, and which one fires depends on the client. A 401 rejects on the
 * global axios object, but `utils/axios` creates its instance with
 * `validateStatus: (status) => status < 500`, so there every 4xx *fulfills* and the
 * rejection branch is unreachable. Roughly half the app's services go through that
 * instance (billing, roles, consents, profile, request, useRequests,
 * useSurgeryAppointments, ...) and each pattern-matches `status === 200`, rejecting
 * generically otherwise — the silent failure this exists to remove. The same invariant is
 * already written down at authService.js:123.
 *
 * This lives in its own module rather than inline in providerSherwood so a test can
 * install it on a real axios instance and assert through it. Called directly, the handler
 * cannot tell you whether it was ever reachable.
 *
 * The response and the error are passed through untouched: callers keep today's control
 * flow, and only the teardown is added.
 */
export function installSessionExpiryInterceptors(clients: ResponseInterceptable[]): void {
    clients.forEach((client) => {
        client.interceptors.response.use(
            (response: any) => {
                if (response?.status === 401) {
                    handleSessionExpiredUnauthorized({ response, config: response.config });
                }
                return response;
            },
            (error: any) => {
                handleSessionExpiredUnauthorized(error);
                return Promise.reject(error);
            },
        );
    });
}
